/**
 * Convert simple LaTeX notation to Unicode for plain-text contexts (e.g. Cytoscape labels).
 * Only handles safe, common substitutions. Complex TeX (\frac, \sum, \int, $$...$$)
 * is stripped of delimiters and returned as-is.
 *
 * Raw text is never mutated — this is a display-only transform.
 */

const SUPERSCRIPTS = {
  '0': '\u2070', '1': '\u00B9', '2': '\u00B2', '3': '\u00B3',
  '4': '\u2074', '5': '\u2075', '6': '\u2076', '7': '\u2077',
  '8': '\u2078', '9': '\u2079',
  '+': '\u207A', '-': '\u207B', '=': '\u207C',
  '(': '\u207D', ')': '\u207E',
  'n': '\u207F', 'i': '\u2071',
};

const SUBSCRIPTS = {
  '0': '\u2080', '1': '\u2081', '2': '\u2082', '3': '\u2083',
  '4': '\u2084', '5': '\u2085', '6': '\u2086', '7': '\u2087',
  '8': '\u2088', '9': '\u2089',
  '+': '\u208A', '-': '\u208B', '=': '\u208C',
  '(': '\u208D', ')': '\u208E',
  'a': '\u2090', 'e': '\u2091', 'i': '\u1D62', 'j': '\u2C7C',
  'k': '\u2096', 'n': '\u2099', 'o': '\u2092', 'r': '\u1D63',
  'x': '\u2093',
};

const SYMBOLS = {
  '\\alpha': '\u03B1', '\\beta': '\u03B2', '\\gamma': '\u03B3',
  '\\delta': '\u03B4', '\\epsilon': '\u03B5', '\\theta': '\u03B8',
  '\\lambda': '\u03BB', '\\mu': '\u03BC', '\\pi': '\u03C0',
  '\\sigma': '\u03C3', '\\phi': '\u03C6', '\\omega': '\u03C9',
  '\\Delta': '\u0394', '\\Sigma': '\u03A3', '\\Pi': '\u03A0',
  '\\Omega': '\u03A9',
  '\\infty': '\u221E', '\\pm': '\u00B1', '\\mp': '\u2213',
  '\\times': '\u00D7', '\\div': '\u00F7', '\\cdot': '\u00B7',
  '\\leq': '\u2264', '\\geq': '\u2265', '\\neq': '\u2260',
  '\\approx': '\u2248', '\\equiv': '\u2261',
  '\\rightarrow': '\u2192', '\\leftarrow': '\u2190',
  '\\Rightarrow': '\u21D2', '\\Leftarrow': '\u21D0',
  '\\sqrt': '\u221A',
  '\\in': '\u2208', '\\notin': '\u2209',
  '\\subset': '\u2282', '\\supset': '\u2283',
  '\\cup': '\u222A', '\\cap': '\u2229',
  '\\forall': '\u2200', '\\exists': '\u2203',
  '\\partial': '\u2202', '\\nabla': '\u2207',
};

// TeX commands that signal complex expressions — skip conversion, just strip delimiters
const COMPLEX_COMMANDS = /\\(?:frac|sum|int|prod|lim|binom|begin|end|matrix|array|sqrt\{)/;

/**
 * Convert a single superscript/subscript group to Unicode.
 * Returns null if any character lacks a Unicode mapping.
 */
function toUnicodeScript(text, map) {
  let result = '';
  for (const ch of text) {
    if (map[ch]) {
      result += map[ch];
    } else {
      return null; // unmapped character — bail out
    }
  }
  return result;
}

/**
 * Convert simple LaTeX in text to Unicode equivalents.
 * Input: raw node text that may contain inline LaTeX like $a^2 - b^2$ or \(x_i\).
 * Output: display string with Unicode math characters.
 */
export function latexToUnicode(text) {
  if (!text || typeof text !== 'string') return text || '';

  let result = text;

  // Strip block math delimiters ($$...$$) — content is too complex for Unicode
  result = result.replace(/\$\$([\s\S]+?)\$\$/g, (_, inner) => {
    return COMPLEX_COMMANDS.test(inner) ? inner.replace(/\\/g, '').trim() : inner.trim();
  });

  // Process inline math: $...$ and \(...\)
  result = result.replace(/\$([^$]+?)\$|\\\((.+?)\\\)/g, (_, g1, g2) => {
    const inner = (g1 || g2).trim();

    // If it contains complex commands, strip delimiters only
    if (COMPLEX_COMMANDS.test(inner)) {
      return inner.replace(/\\/g, '').trim();
    }

    return convertInlineMath(inner);
  });

  // Also handle bare \[...\] block delimiters
  result = result.replace(/\\\[([\s\S]+?)\\\]/g, (_, inner) => {
    return COMPLEX_COMMANDS.test(inner) ? inner.replace(/\\/g, '').trim() : inner.trim();
  });

  // Handle bare superscripts/subscripts outside of math delimiters
  // Common in LLM-generated concept labels like "a^2 - b^2 = 45"
  result = result.replace(/\^{([^}]+)}/g, (_, group) => {
    const converted = toUnicodeScript(group, SUPERSCRIPTS);
    return converted !== null ? converted : '^(' + group + ')';
  });
  result = result.replace(/\^([0-9n+\-i])/g, (_, ch) => {
    return SUPERSCRIPTS[ch] || '^' + ch;
  });
  result = result.replace(/_{([^}]+)}/g, (_, group) => {
    const converted = toUnicodeScript(group, SUBSCRIPTS);
    return converted !== null ? converted : '_(' + group + ')';
  });
  result = result.replace(/_([0-9a-einojkrx])(?![a-zA-Z])/g, (_, ch) => {
    return SUBSCRIPTS[ch] || '_' + ch;
  });

  return result;
}

/**
 * Convert an inline LaTeX expression (without delimiters) to Unicode.
 */
function convertInlineMath(expr) {
  let result = expr;

  // Replace named symbols first (longest match first to avoid partial matches)
  const sortedSymbols = Object.keys(SYMBOLS).sort((a, b) => b.length - a.length);
  for (const sym of sortedSymbols) {
    result = result.split(sym).join(SYMBOLS[sym]);
  }

  // Superscripts: x^{2n} or x^2
  result = result.replace(/\^{([^}]+)}/g, (_, group) => {
    const converted = toUnicodeScript(group, SUPERSCRIPTS);
    return converted !== null ? converted : '^(' + group + ')';
  });
  result = result.replace(/\^([0-9n+\-i])/g, (_, ch) => {
    return SUPERSCRIPTS[ch] || '^' + ch;
  });

  // Subscripts: x_{2n} or x_2
  result = result.replace(/_{([^}]+)}/g, (_, group) => {
    const converted = toUnicodeScript(group, SUBSCRIPTS);
    return converted !== null ? converted : '_(' + group + ')';
  });
  result = result.replace(/_([0-9a-einojkrx])/g, (_, ch) => {
    return SUBSCRIPTS[ch] || '_' + ch;
  });

  // Clean up remaining backslashes from unrecognized commands
  result = result.replace(/\\([a-zA-Z]+)/g, (_, cmd) => cmd);

  // Clean up braces used for grouping
  result = result.replace(/[{}]/g, '');

  return result;
}
